import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import IncludePropertyInEvents from "./include-feature-flag-property-in-backend-events.mdx";
import APIOverrideServerProperties from './override-server-properties/api.mdx';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const MultiLanguage = makeShortcode("MultiLanguage");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are 3 steps to implement feature flags using the PostHog API:`}</p>
    <h3 {...{
      "id": "step-1-evaluate-the-feature-flag-value-using-flags"
    }}>{`Step 1: Evaluate the feature flag value using `}<inlineCode parentName="h3">{`flags`}</inlineCode></h3>
    <p><inlineCode parentName="p">{`flags`}</inlineCode>{` is the endpoint used to determine if a given flag is enabled for a certain user or not.`}</p>
    <h4 {...{
      "id": "request"
    }}>{`Request`}</h4>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`# Basic request (flags only)
curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "groups" : {
        "group_type": "group_id"
    }
}' "<ph_client_api_host>/flags?v=2"

# With configuration (flags + PostHog config)
curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "groups" : {
        "group_type": "group_id"
    }
}' "<ph_client_api_host>/flags?v=2&config=true"
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

# Basic request (flags only)
url = "<ph_client_api_host>/flags?v=2"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "user distinct id",
    "groups": {
        "group_type": "group_id"
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response.json())

# With configuration (flags + PostHog config)
url_with_config = "<ph_client_api_host>/flags?v=2&config=true"
response_with_config = requests.post(url_with_config, headers=headers, data=json.dumps(payload))
print(response_with_config.json())
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`import fetch from "node-fetch";

async function sendFlagsRequest() {
    const headers = {
        "Content-Type": "application/json",
    };
    const payload = {
        api_key: "<ph_project_api_key>",
        distinct_id: "user distinct id",
        groups: {
            group_type: "group_id",
        },
    };

    // Basic request (flags only)
    const url = "<ph_client_api_host>/flags?v=2";
    const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    console.log(data);

    // With configuration (flags + PostHog config)
    const urlWithConfig = "<ph_client_api_host>/flags?v=2&config=true";
    const responseWithConfig = await fetch(urlWithConfig, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
    });
    const dataWithConfig = await responseWithConfig.json();
    console.log(dataWithConfig);
}

sendFlagsRequest();
`}</code></pre>
    </MultiLanguage>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` The `}<inlineCode parentName="p">{`groups`}</inlineCode>{` key is only required for group-based feature flags. If you use it, replace `}<inlineCode parentName="p">{`group_type`}</inlineCode>{` and `}<inlineCode parentName="p">{`group_id`}</inlineCode>{` with the values for your group such as `}<inlineCode parentName="p">{`company: "Twitter"`}</inlineCode>{`.`}</p>
    </blockquote>
    <h4 {...{
      "id": "using-evaluation-environment-tags-and-runtime-filtering-without-sdks"
    }}>{`Using evaluation environment tags and runtime filtering without SDKs`}</h4>
    <p>{`When making direct API calls to the `}<inlineCode parentName="p">{`/flags`}</inlineCode>{` endpoint, you can control which flags are evaluated using evaluation environment tags and runtime filtering.`}</p>
    <h5 {...{
      "id": "evaluation-environments"
    }}>{`Evaluation environments`}</h5>
    <p>{`To filter flags by evaluation environment, include the `}<inlineCode parentName="p">{`evaluation_environments`}</inlineCode>{` field in your request body:`}</p>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "distinct_id_of_your_user",
    "evaluation_environments": ["production", "web"]
}' "<ph_client_api_host>/flags?v=2"
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/flags?v=2"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>",
    "distinct_id": "user distinct id",
    "evaluation_environments": ["production", "web"]
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response.json())
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-javascript"
        }}>{`const response = await fetch("<ph_client_api_host>/flags?v=2", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
    },
    body: JSON.stringify({
        api_key: "<ph_project_api_key>",
        distinct_id: "user-distinct-id",
        evaluation_environments: ["production", "web"]
    }),
});
const data = await response.json();
`}</code></pre>
    </MultiLanguage>
    <p>{`Only flags where at least one evaluation tag matches (or flags with no tags at all) will be returned. For example:`}</p>
    <ul>
      <li parentName="ul">{`Flag with evaluation environment tags `}<inlineCode parentName="li">{`["production", "api", "backend"]`}</inlineCode>{` + request with `}<inlineCode parentName="li">{`["production", "web"]`}</inlineCode>{` = ✅ Flag evaluates ("production" matches)`}</li>
      <li parentName="ul">{`Flag with evaluation environment tags `}<inlineCode parentName="li">{`["staging", "api"]`}</inlineCode>{` + request with `}<inlineCode parentName="li">{`["production", "web"]`}</inlineCode>{` = ❌ Flag doesn't evaluate (no tags match)`}</li>
      <li parentName="ul">{`Flag with evaluation environment tags `}<inlineCode parentName="li">{`["web", "mobile"]`}</inlineCode>{` + request with `}<inlineCode parentName="li">{`["production", "web"]`}</inlineCode>{` = ✅ Flag evaluates ("web" matches)`}</li>
      <li parentName="ul">{`Flag with no evaluation environment tags = ✅ Always evaluates (backward compatibility)`}</li>
    </ul>
    <h5 {...{
      "id": "runtime-detection"
    }}>{`Runtime detection`}</h5>
    <p>{`Evaluation runtime (server vs. client) is automatically detected based on your request headers and user-agent. This determines which flags are available based on their runtime setting (server-only, client-only, or all).`}</p>
    <p><strong parentName="p">{`How runtime is detected:`}</strong></p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`User-Agent patterns`}</strong>{` - The system analyzes the User-Agent header:`}</p>
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Client-side patterns`}</strong>{`: `}<inlineCode parentName="li">{`Mozilla/`}</inlineCode>{`, `}<inlineCode parentName="li">{`Chrome/`}</inlineCode>{`, `}<inlineCode parentName="li">{`Safari/`}</inlineCode>{`, `}<inlineCode parentName="li">{`Firefox/`}</inlineCode>{`, `}<inlineCode parentName="li">{`Edge/`}</inlineCode>{` (browsers), or mobile SDKs like `}<inlineCode parentName="li">{`posthog-android/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-ios/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-react-native/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-flutter/`}</inlineCode></li>
          <li parentName="ul"><strong parentName="li">{`Server-side patterns`}</strong>{`: `}<inlineCode parentName="li">{`posthog-python/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-ruby/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-php/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-java/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-go/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-node/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-dotnet/`}</inlineCode>{`, `}<inlineCode parentName="li">{`posthog-elixir/`}</inlineCode>{`, `}<inlineCode parentName="li">{`python-requests/`}</inlineCode>{`, `}<inlineCode parentName="li">{`curl/`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Browser-specific headers`}</strong>{` - Presence of these headers indicates client-side:`}</p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Origin`}</inlineCode>{` header`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Referer`}</inlineCode>{` header  `}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Sec-Fetch-Mode`}</inlineCode>{` header`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`Sec-Fetch-Site`}</inlineCode>{` header`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Default behavior`}</strong>{` - If runtime can't be determined, the system includes flags with no runtime requirement and those set to "all"`}</p>
      </li>
    </ol>
    <p><strong parentName="p">{`Examples of runtime detection:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Browser fetch - Detected as CLIENT runtime
// Will receive: client-only flags + "all" flags
// Won't receive: server-only flags
const response = await fetch("<ph_client_api_host>/flags?v=2", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        // Browser automatically adds Origin, Referer, Sec-Fetch-* headers
    },
    body: JSON.stringify({
        api_key: "<ph_project_api_key>",
        distinct_id: "user-id"
    })
});
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`# Python requests - Detected as SERVER runtime
# Will receive: server-only flags + "all" flags
# Won't receive: client-only flags
import requests

response = requests.post(
    "<ph_client_api_host>/flags?v=2",
    json={
        "api_key": "<ph_project_api_key>",
        "distinct_id": "user-id"
    }
    # python-requests/ in User-Agent indicates server-side
)
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`# curl - Detected as SERVER runtime
# Will receive: server-only flags + "all" flags
# Won't receive: client-only flags
curl -v -L --header "Content-Type: application/json" -d '{
    "api_key": "<ph_project_api_key>",
    "distinct_id": "user-id"
}' "<ph_client_api_host>/flags?v=2"
# curl/ in User-Agent indicates server-side
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Node.js with custom User-Agent - Control runtime detection
const response = await fetch("<ph_client_api_host>/flags?v=2", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        "User-Agent": "posthog-node/3.0.0"  // Explicitly indicates server-side
    },
    body: JSON.stringify({
        api_key: "<ph_project_api_key>",
        distinct_id: "user-id"
    })
});
`}</code></pre>
    <h5 {...{
      "id": "combining-evaluation-environment-tags-and-runtime-filtering"
    }}>{`Combining evaluation environment tags and runtime filtering`}</h5>
    <p>{`Both features work together as sequential filters:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Example: Production web client
const response = await fetch("<ph_client_api_host>/flags?v=2", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        // Browser headers will trigger client runtime detection
    },
    body: JSON.stringify({
        api_key: "<ph_project_api_key>",
        distinct_id: "user-id",
        evaluation_environments: ["production", "web"]
    })
});

// This request will only receive flags that:
// 1. Have runtime set to "client" OR "all" (due to browser headers)
// AND
// 2. Have evaluation environment tags matching "production" OR "web" (or no tags)
`}</code></pre>
    <p>{`This allows precise control over which flags are evaluated in different contexts, helping optimize costs and improve security by ensuring flags only evaluate where intended.`}</p>
    <h4 {...{
      "id": "response"
    }}>{`Response`}</h4>
    <p>{`The response varies depending on whether you include the `}<inlineCode parentName="p">{`config=true`}</inlineCode>{` query parameter:`}</p>
    <h5 {...{
      "id": "basic-response-flagsv2"
    }}>{`Basic response (`}<inlineCode parentName="h5">{`/flags?v=2`}</inlineCode>{`)`}</h5>
    <p>{`Use this endpoint when you only need to evaluate feature flags. It returns a response with just the flag evaluation results:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "flags": {
    "my-awesome-flag": {
      "key": "my-awesome-flag", 
      "enabled": true,
      "reason": {
        "code": "condition_match",
        "condition_index": 0,
        "description": "Condition set 1 matched"
      },
      "metadata": {
        "id": 1,
        "version": 1,
        "payload": "{\\"example\\": \\"json\\", \\"payload\\": \\"value\\"}"
      }
    },
    "my-multivariate-flag" :{
      "key":"my-multivariate-flag",
      "enabled": true,
      "variant": "some-string-value",
      "reason": {
        "code": "condition_match",
        "condition_index": 1,
        "description": "Condition set 2 matched"
      },
      "metadata": {
        "id": 2,
        "version": 42,
      }
    },
    "flag-thats-not-on": {
      "key": "flag-thats-not-on",
      "enabled": false,
      "reason": {
        "code": "no_condition_match",
        "condition_index": 0,
        "description": "No condition sets matched"
      },
      "metadata": {
        "id": 3,
        "version": 1
      }
    }
  },
  "errorsWhileComputingFlags": false,
  "requestId": "550e8400-e29b-41d4-a716-446655440000"
}
`}</code></pre>
    <h5 {...{
      "id": "full-response-with-configuration-flagsv2configtrue"
    }}>{`Full response with configuration (`}<inlineCode parentName="h5">{`/flags?v=2&config=true`}</inlineCode>{`)`}</h5>
    <p>{`Use this endpoint when you need both feature flag evaluation and PostHog configuration information (useful for client-side SDKs that need to initialize PostHog):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "config": {
    "enable_collect_everything": true
  },
  "toolbarParams": {},
  "errorsWhileComputingFlags": false,
  "isAuthenticated": false,
  "requestId": "550e8400-e29b-41d4-a716-446655440000",
  "supportedCompression": [
    "gzip",
    "lz64"
  ],
  "flags": {
    "my-awesome-flag": {
      "key": "my-awesome-flag", 
      "enabled": true,
      "reason": {
        "code": "condition_match",
        "condition_index": 0,
        "description": "Condition set 1 matched"
      },
      "metadata": {
        "id": 1,
        "version": 1,
        "payload": "{\\"example\\": \\"json\\", \\"payload\\": \\"value\\"}"
      }
    },
    "my-multivariate-flag" :{
      "key":"my-multivariate-flag",
      "enabled": true,
      "variant": "some-string-value",
      "reason": {
        "code": "condition_match",
        "condition_index": 1,
        "description": "Condition set 2 matched"
      },
      "metadata": {
        "id": 2,
        "version": 42,
      }
    },
    "flag-thats-not-on": {
      "key": "flag-thats-not-on",
      "enabled": false,
      "reason": {
        "code": "no_condition_match",
        "condition_index": 0,
        "description": "No condition sets matched"
      },
      "metadata": {
        "id": 3,
        "version": 1
      }
    }
  }
}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` `}<inlineCode parentName="p">{`errorsWhileComputingFlags`}</inlineCode>{` will return `}<inlineCode parentName="p">{`true`}</inlineCode>{` if we didn't manage to compute some flags (for example, if there's an `}<a parentName="p" {...{
          "href": "https://status.posthog.com/"
        }}>{`ongoing incident involving flag evaluation`}</a>{`). `}</p>
      <p parentName="blockquote">{`This enables partial updates to currently active flags in your clients.`}</p>
    </blockquote>
    <h4 {...{
      "id": "quota-limiting"
    }}>{`Quota limiting`}</h4>
    <p>{`If your organization exceeds its feature flag quota, the `}<inlineCode parentName="p">{`/flags`}</inlineCode>{` endpoint will return a modified response with `}<inlineCode parentName="p">{`quotaLimited`}</inlineCode>{`.`}</p>
    <p>{`For basic response (`}<inlineCode parentName="p">{`/flags?v=2`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "flags": {},
  "errorsWhileComputingFlags": false,
  "quotaLimited": ["feature_flags"],
  "requestId": "d4d89b14-9619-4627-adf2-01b761691c2e"
}
`}</code></pre>
    <p>{`For full response with configuration (`}<inlineCode parentName="p">{`/flags?v=2&config=true`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "config": {
    "enable_collect_everything": true
  },
  "toolbarParams": {},
  "isAuthenticated": false,
  "supportedCompression": [
    "gzip",
    "lz64"
  ],
  "flags": {},
  "errorsWhileComputingFlags": false,
  "quotaLimited": ["feature_flags"],
  "requestId": "d4d89b14-9619-4627-adf2-01b761691c2e"
  // ... other fields, not relevant to feature flags
}
`}</code></pre>
    <p>{`When you receive a response with `}<inlineCode parentName="p">{`quotaLimited`}</inlineCode>{` containing `}<inlineCode parentName="p">{`"feature_flags"`}</inlineCode>{`, it means:`}</p>
    <ol>
      <li parentName="ol">{`Your feature flag evaluations have been temporarily paused because you've exceeded your feature flag quota`}</li>
      <li parentName="ol">{`If you want to continue evaluating feature flags, you can increase your quota in `}<a parentName="li" {...{
          "href": "https://us.posthog.com/organization/billing"
        }}>{`your billing settings`}</a>{` under `}<strong parentName="li">{`Feature flags & Experiments`}</strong>{` or `}<a parentName="li" {...{
          "href": "https://us.posthog.com/#panel=support%3Asupport%3Abilling%3A%3Atrue"
        }}>{`contact support`}</a></li>
    </ol>

    <IncludePropertyInEvents mdxType="IncludePropertyInEvents" />
    <p>{`To do this, include the `}<inlineCode parentName="p">{`$feature/feature_flag_name`}</inlineCode>{` property in your event:`}</p>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "event": "your_event_name",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
      "$feature/feature-flag-key": "variant-key" # Replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    }
}' <ph_client_api_host>/i/v0/e/ 
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/i/v0/e/"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>", 
    "event": "your_event_name",
    "distinct_id": "distinct_id_of_your_user,
    "properties": {
      "$feature/feature-flag-key": "variant-key" # Replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response)
`}</code></pre>
    </MultiLanguage>
    <h3 {...{
      "id": "step-3-send-a-feature_flag_called-event"
    }}>{`Step 3: Send a `}<inlineCode parentName="h3">{`$feature_flag_called`}</inlineCode>{` event`}</h3>
    <p>{`To track usage of your feature flag and view related analytics in PostHog, submit the `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` event whenever you check a feature flag value in your code.`}</p>
    <p>{`You need to include two properties with this event:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag_response`}</inlineCode>{`: This is the name of the variant the user has been assigned to e.g., "control" or "test"`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`$feature_flag`}</inlineCode>{`: This is the key of the feature flag in your experiment.`}</li>
    </ol>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-shell"
        }}>{`curl -v -L --header "Content-Type: application/json" -d '  {
    "api_key": "<ph_project_api_key>",
    "event": "$feature_flag_called",
    "distinct_id": "distinct_id_of_your_user",
    "properties": {
      "$feature_flag": "feature-flag-key",
      "$feature_flag_response": "variant-name"
    }
}' <ph_client_api_host>/i/v0/e/ 
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import requests
import json

url = "<ph_client_api_host>/i/v0/e/"
headers = {
    "Content-Type": "application/json"
}
payload = {
    "api_key": "<ph_project_api_key>", 
    "event": "feature_flag_called",
    "distinct_id": "distinct_id_of_your_user,
    "properties": {
      "$feature_flag": "feature-flag-key",
      "$feature_flag_response": "variant-name"
    }
}
response = requests.post(url, headers=headers, data=json.dumps(payload))
print(response)
`}</code></pre>
    </MultiLanguage>

    <APIOverrideServerProperties mdxType="APIOverrideServerProperties" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      